import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table, Form } from 'react-bootstrap';
import Select from 'react-select';
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { numberFormat, getThisFiscalYearRange } from '../helpers/GlobalHelper';
import { postRequestOptions, getRequestOptions } from '../helpers/Fetchwrapper';
import { Link, useLocation } from 'react-router-dom';
import { ReportSelectOption } from './LedgerStatementReport';

type ProjectOption = ReportSelectOption & { accounting_startup_date?: string | null };
type LedgerOption = ReportSelectOption & {
    ledgerName?: string;
    ledgerCode?: string;
    ledgerPath?: string;
    bankLabel?: string;
    accountNumber?: string;
};

const defaultProjectOption: ProjectOption = { label: 'Select Project', value: '' };
const defaultLedgerOption: LedgerOption = { label: 'Select Bank Book Ledger', value: '' };
const fiscalYearRange = getThisFiscalYearRange();

type ViewMode = 'cleared' | 'all';

interface StatementRow {
    sn: number;
    idAccVouchers: number;
    voucherDate: string;
    voucherNo: string;
    particulars: string;
    voucherType: string;
    modeOfTransaction: string;
    chequeNo: string;
    bankDate: string;
    drAmount: number;
    crAmount: number;
}

interface StatementPayload {
    header: {
        projectName: string;
        ledgerName: string;
        ledgerCode: string;
        ledgerPath: string;
        bankLabel: string;
        fromDate: string;
        toDate: string;
        viewMode: ViewMode;
    };
    opening: {
        drAmount: number;
        crAmount: number;
    };
    rows: StatementRow[];
    summary: {
        totalDr: number;
        totalCr: number;
        notReflectedDr: number;
        notReflectedCr: number;
        bookBalance: number;
        bookBalanceType: string;
        bankBalance: number;
        bankBalanceType: string;
    };
}

const formatAmount = (value: number | undefined) => numberFormat(Number(value || 0));

const formatDisplayDate = (value?: string) => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
};

const filterBankLedgerOption = (option: { data: LedgerOption; label?: string }, input: string) => {
    const keyword = String(input || '').toLowerCase().trim();
    if (!keyword) {
        return true;
    }

    const data = option.data || {};
    const haystacks = [
        option.label,
        data.label,
        data.ledgerName,
        data.ledgerCode,
        data.bankLabel,
        data.accountNumber,
        data.ledgerPath,
    ];

    return haystacks.some((value) => String(value || '').toLowerCase().includes(keyword));
};

const rowMatchesSearch = (row: StatementRow, keyword: string) => {
    if (!keyword) {
        return true;
    }
    const haystacks = [
        row.voucherNo,
        row.particulars,
        row.voucherType,
        row.modeOfTransaction,
        row.chequeNo,
        row.bankDate,
        row.voucherDate,
    ];
    return haystacks.some((value) => String(value || '').toLowerCase().includes(keyword));
};

const BankReconciliationReport: React.FC = () => {
    const location = useLocation();
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const [loading, setLoading] = useState(false);
    const [projects, setProjects] = useState<ProjectOption[]>([]);
    const [project, setProject] = useState<ProjectOption>(defaultProjectOption);
    const [ledgers, setLedgers] = useState<LedgerOption[]>([]);
    const [ledger, setLedger] = useState<LedgerOption>(defaultLedgerOption);
    const [reportData, setReportData] = useState<StatementPayload | null>(null);
    const [viewMode, setViewMode] = useState<ViewMode>((query.get('viewMode') as ViewMode) || 'cleared');
    const [searchText, setSearchText] = useState('');

    const [formData, setFormData] = useState({
        idProjects: query.get('idProjects') ?? '',
        idAccLedgers: query.get('idAccLedgers') ?? '',
        fromDate: query.get('fromDate') ?? fiscalYearRange.fromDate,
        toDate: query.get('toDate') ?? fiscalYearRange.toDate,
        viewMode: (query.get('viewMode') as ViewMode) || 'cleared',
    });

    const hasInitialQuery = useMemo(
        () => Boolean(query.get('idProjects') && query.get('idAccLedgers') && query.get('fromDate') && query.get('toDate')),
        [query]
    );
    const autoLoadTriggeredRef = useRef(false);

    const fetchProjects = () => {
        fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const projectOptions: ProjectOption[] = (resp?.data || []).map((item: any) => ({
                    label: item.name,
                    value: item.id,
                    accounting_startup_date: item.accounting_startup_date ?? '',
                }));
                setProjects(projectOptions);

                const queryProjectId = query.get('idProjects');
                let nextProject = defaultProjectOption;
                if (queryProjectId) {
                    nextProject = projectOptions.find((opt) => String(opt.value) === String(queryProjectId)) ?? defaultProjectOption;
                } else if (projectOptions.length > 0) {
                    nextProject = projectOptions[0];
                }

                setProject(nextProject);
                setFormData((prev) => ({
                    ...prev,
                    idProjects: nextProject.value !== '' ? String(nextProject.value) : '',
                    fromDate: query.get('fromDate') || nextProject.accounting_startup_date || prev.fromDate,
                }));
            })
            .catch((error) => console.log(error, 'catch the hoop'));
    };

    const fetchLedgers = () => {
        fetch(process.env.REACT_APP_API_URL + 'reports/bank-book-ledgers', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const ledgerOptions: LedgerOption[] = (resp?.data || []).map((item: any) => {
                    const ledgerName = item.ledgerName || '';
                    const bankLabel = item.bankLabel || '';
                    const displayLabel = ledgerName && bankLabel && ledgerName !== bankLabel
                        ? `${ledgerName} — ${bankLabel}`
                        : (bankLabel || ledgerName);

                    return {
                        label: displayLabel,
                        value: item.idAccLedgers,
                        ledgerName,
                        ledgerCode: item.ledgerCode || '',
                        ledgerPath: item.ledgerPath || '',
                        bankLabel,
                        accountNumber: item.accountNumber || '',
                    };
                });
                setLedgers(ledgerOptions);

                const queryLedgerId = query.get('idAccLedgers') || formData.idAccLedgers;
                if (queryLedgerId) {
                    const found = ledgerOptions.find((opt) => String(opt.value) === String(queryLedgerId));
                    if (found) {
                        setLedger(found);
                    }
                }
            })
            .catch((error) => console.log(error, 'catch the hoop'));
    };

    useEffect(() => {
        fetchProjects();
        fetchLedgers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleProjectChange = (selectedProject: ProjectOption | null) => {
        const nextProject = selectedProject ?? defaultProjectOption;
        setProject(nextProject);
        setFormData((prev) => ({
            ...prev,
            idProjects: nextProject.value !== '' ? String(nextProject.value) : '',
            fromDate: nextProject.accounting_startup_date || prev.fromDate,
        }));
    };

    const handleLedgerChange = (selectedLedger: LedgerOption | null) => {
        if (!selectedLedger) {
            setLedger(defaultLedgerOption);
            setFormData((prev) => ({ ...prev, idAccLedgers: '' }));
            return;
        }
        setLedger(selectedLedger);
        setFormData((prev) => ({
            ...prev,
            idAccLedgers: selectedLedger.value !== '' ? String(selectedLedger.value) : '',
        }));
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleViewModeChange = (mode: ViewMode) => {
        setViewMode(mode);
        setFormData((prev) => ({ ...prev, viewMode: mode }));
    };

    useEffect(() => {
        if (!reportData) {
            return;
        }
        handleViewReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode]);

    const handleViewReport = useCallback(async () => {
        if (!formData.idProjects) {
            toast.error('Project is required.', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (!formData.idAccLedgers) {
            toast.error('Bank Book Ledger is required.', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (!formData.fromDate || !formData.toDate) {
            toast.error('From Date and To Date are required.', { position: 'bottom-right', autoClose: 4000 });
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'reports/bank-reconciliation-statement',
                postRequestOptions({ ...formData, viewMode })
            );
            const data = await response.json();

            if (data.success) {
                setReportData(data.data || null);
                setSearchText('');
            } else {
                setReportData(null);
                const errors = data?.errors;
                const messages = Array.isArray(errors)
                    ? errors
                    : errors
                        ? Object.values(errors).flat()
                        : [data?.message || 'Failed to load report.'];
                messages.forEach((error: string) => toast.error(error, { position: 'bottom-right', autoClose: 4000 }));
            }
        } catch (error) {
            console.error('Error fetching report:', error);
            toast.error('Failed to load report. Please try again later.', { position: 'bottom-right', autoClose: 4000 });
        } finally {
            setLoading(false);
        }
    }, [formData, viewMode]);

    useEffect(() => {
        if (
            hasInitialQuery &&
            !autoLoadTriggeredRef.current &&
            formData.idProjects &&
            formData.idAccLedgers &&
            formData.fromDate &&
            formData.toDate
        ) {
            handleViewReport();
            autoLoadTriggeredRef.current = true;
        }
    }, [formData.idProjects, formData.idAccLedgers, formData.fromDate, formData.toDate, hasInitialQuery, handleViewReport]);

    const visibleRows = useMemo(() => {
        const keyword = searchText.trim().toLowerCase();
        const rows = reportData?.rows || [];
        if (!keyword) {
            return rows;
        }
        return rows.filter((row) => rowMatchesSearch(row, keyword)).map((row, index) => ({ ...row, sn: index + 1 }));
    }, [reportData, searchText]);

    const visibleTotals = useMemo(() => ({
        totalDr: visibleRows.reduce((sum, row) => sum + Number(row.drAmount || 0), 0),
        totalCr: visibleRows.reduce((sum, row) => sum + Number(row.crAmount || 0), 0),
    }), [visibleRows]);

    const queryParams = useMemo(() => {
        const params = new URLSearchParams();
        Object.entries({ ...formData, viewMode }).forEach(([key, value]) => {
            if (value) {
                params.append(key, String(value));
            }
        });
        if (searchText.trim()) {
            params.append('search', searchText.trim());
        }
        return params;
    }, [formData, viewMode, searchText]);

    const handlePrint = () => {
        if (!reportData) {
            toast.error('Please load the report before printing.', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        window.location.href = `${process.env.REACT_APP_BACKEND}bank_reconciliation_statement_print?` + queryParams.toString();
    };

    const summary = reportData?.summary;

    return (
        <>
            <Card className='mb-4 bank-reconciliation-filters'>
                <Card.Body>
                    <div className='row'>
                        <div className='col-md-3'>
                            <Form.Group className="mb-3" controlId="project">
                                <Form.Label>Project<span className='required'></span></Form.Label>
                                <Select
                                    value={project}
                                    onChange={handleProjectChange}
                                    options={projects}
                                    menuPosition="fixed"
                                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    isClearable={true}
                                />
                            </Form.Group>
                        </div>
                        <div className='col-md-3'>
                            <Form.Group className="mb-3" controlId="ledger">
                                <Form.Label>Bank Book Ledger<span className='required'></span></Form.Label>
                                <Select
                                    value={ledger.value ? ledger : null}
                                    onChange={handleLedgerChange}
                                    options={ledgers}
                                    placeholder="Select Bank Book Ledger"
                                    isClearable={true}
                                    isSearchable={true}
                                    filterOption={filterBankLedgerOption}
                                    menuPosition="fixed"
                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        menu: base => ({ ...base, zIndex: 9999 }),
                                    }}
                                    noOptionsMessage={({ inputValue }) =>
                                        inputValue ? `No bank ledger matching "${inputValue}"` : 'No bank book ledger found'
                                    }
                                />
                            </Form.Group>
                        </div>
                        <div className='col-md-2'>
                            <Form.Group className="mb-3" controlId="fromDate">
                                <Form.Label>From Date<span className='required'></span></Form.Label>
                                <Form.Control type="date" name='fromDate' value={formData.fromDate} onChange={handleChange} />
                            </Form.Group>
                        </div>
                        <div className='col-md-2'>
                            <Form.Group className="mb-3" controlId="toDate">
                                <Form.Label>To Date<span className='required'></span></Form.Label>
                                <Form.Control type="date" name='toDate' value={formData.toDate} onChange={handleChange} />
                            </Form.Group>
                        </div>
                        <div className='col-md-2'>
                            <div className="d-flex flex-wrap mt-7">
                                <button type='button' className='btn btn-sm btn-primary p-3 me-2' onClick={handleViewReport} disabled={loading}>
                                    {loading && <span className="spinner-border spinner-border-sm me-2"></span>}
                                    View Report
                                </button>
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {reportData?.header && (
                <div className='text-center mb-4'>
                    <h4 className='fw-bolder mb-1'>Bank Reconciliation ({viewMode === 'cleared' ? 'Clear' : 'All'})</h4>
                    <div className='fw-bold'>{reportData.header.projectName}</div>
                    <div className='text-muted'>
                        {formatDisplayDate(reportData.header.fromDate)} to {formatDisplayDate(reportData.header.toDate)}
                    </div>
                    <div className='fw-bold mt-1'>{reportData.header.bankLabel || reportData.header.ledgerName}</div>
                    {reportData.header.ledgerPath && (
                        <div className='fs-8 text-muted'>{reportData.header.ledgerPath}</div>
                    )}
                </div>
            )}

            {reportData && (
                <div className='d-flex flex-wrap align-items-center justify-content-between mb-3'>
                    <div className='btn-group mb-2' role='group'>
                        <button type='button' className={`btn btn-sm ${viewMode === 'cleared' ? 'btn-primary' : 'btn-light-primary'}`} onClick={() => handleViewModeChange('cleared')}>
                            Cleared
                        </button>
                        <button type='button' className={`btn btn-sm ${viewMode === 'all' ? 'btn-primary' : 'btn-light-primary'}`} onClick={() => handleViewModeChange('all')}>
                            All Transactions
                        </button>
                    </div>
                    <div className='d-flex align-items-center mb-2'>
                        <Form.Control
                            type='text'
                            placeholder='Search all columns'
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                            style={{ minWidth: '220px' }}
                            className='me-2'
                        />
                        <button type='button' className='btn btn-sm btn-light-primary p-3' onClick={handlePrint} disabled={loading}>
                            <i className='fas fa-print me-1'></i>
                            Print PDF
                        </button>
                    </div>
                </div>
            )}

            <div className='row'>
                <div className='col-12 trial-balance-container table-responsive'>
                    <Table bordered hover size='sm' className='mb-0'>
                        <thead className="sticky-top bg-white">
                            <tr>
                                <th className='text-center' style={{ width: '4%' }}>SN.</th>
                                <th style={{ width: '9%' }}>Date</th>
                                <th style={{ width: '13%' }}>Voucher No</th>
                                <th style={{ width: '18%' }}>Particulars</th>
                                <th style={{ width: '11%' }}>Voucher Type</th>
                                <th style={{ width: '11%' }}>Mode of Transaction</th>
                                <th style={{ width: '8%' }}>Cheque No</th>
                                <th style={{ width: '9%' }}>Bank Date</th>
                                <th className='text-end' style={{ width: '9%' }}>Debit</th>
                                <th className='text-end' style={{ width: '8%' }}>Credit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!reportData && (
                                <tr>
                                    <td colSpan={10} className="text-center py-8 text-muted">
                                        Select Project, Bank Book Ledger, From Date and To Date, then click View Report.
                                    </td>
                                </tr>
                            )}
                            {reportData && (
                                <tr className='fw-bold table-light'>
                                    <td></td>
                                    <td>{formatDisplayDate(reportData.header.fromDate)}</td>
                                    <td></td>
                                    <td>Opening Balance</td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td className='text-end'>{formatAmount(reportData.opening.drAmount)}</td>
                                    <td className='text-end'>{formatAmount(reportData.opening.crAmount)}</td>
                                </tr>
                            )}
                            {reportData && visibleRows.length === 0 && (
                                <tr>
                                    <td colSpan={10} className="text-center py-6 text-muted">No transactions found.</td>
                                </tr>
                            )}
                            {visibleRows.map((row) => (
                                <tr key={`${row.idAccVouchers}-${row.sn}`}>
                                    <td className='text-center'>{row.sn}</td>
                                    <td>{formatDisplayDate(row.voucherDate)}</td>
                                    <td>
                                        <Link to={`transaction-details/${row.idAccVouchers}`} className="text-primary">
                                            {row.voucherNo}
                                        </Link>
                                    </td>
                                    <td>{row.particulars || '-'}</td>
                                    <td>{row.voucherType}</td>
                                    <td>{row.modeOfTransaction || '-'}</td>
                                    <td>{row.chequeNo || '-'}</td>
                                    <td>{formatDisplayDate(row.bankDate) || '-'}</td>
                                    <td className='text-end'>{formatAmount(row.drAmount)}</td>
                                    <td className='text-end'>{formatAmount(row.crAmount)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </div>

            {reportData && summary && (
                <div className='d-flex justify-content-end mt-4'>
                    <Table bordered size='sm' style={{ maxWidth: '520px' }}>
                        <tbody>
                            <tr className='fw-bold'>
                                <td>Total</td>
                                <td className='text-end'>{formatAmount(visibleTotals.totalDr)}</td>
                                <td className='text-end'>{formatAmount(visibleTotals.totalCr)}</td>
                            </tr>
                            <tr>
                                <td>Amount not reflected in Bank</td>
                                <td className='text-end'>{formatAmount(summary.notReflectedDr)}</td>
                                <td className='text-end'>{formatAmount(summary.notReflectedCr)}</td>
                            </tr>
                            <tr className='fw-bold table-light'>
                                <td>Balance as per Company Book</td>
                                <td className='text-end' colSpan={2}>
                                    {summary.bookBalanceType} {formatAmount(summary.bookBalance)}
                                </td>
                            </tr>
                            <tr className='fw-bold table-light'>
                                <td>Balance as per Bank</td>
                                <td className='text-end' colSpan={2}>
                                    {summary.bankBalanceType} {formatAmount(summary.bankBalance)}
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </div>
            )}
        </>
    );
};

export default BankReconciliationReport;
